import { useAuthStore } from '~/store/auth.store';

/**
 * Which Configuration Manager surface the current user/tenant should see.
 * `global` reads/writes the platform-wide `/parameters` API; `subsidiary`
 * reads/writes the per-tenant `/subsidiary-parameters` API for the active
 * tenant.
 */
export type ConfigurationScope =
  | { mode: 'global' }
  | { mode: 'subsidiary'; subsidiaryId: string; subsidiaryName: string };

/**
 * Resolves global vs subsidiary scope for the Configuration Manager.
 *
 * Global parameters are only for Superadmins whose *active* tenant is the
 * system org (`isSystem: true`) — everyone else (subsidiary admins on their
 * own tenant, or a superadmin who has switched into a non-system subsidiary)
 * gets the subsidiary-scoped view of their active tenant. Fails safe to
 * `subsidiary` mode if the active tenant can't be resolved, so a lookup miss
 * never accidentally grants the global view.
 *
 * This only decides which client-side API/UI path to use — the backend is
 * the authoritative enforcement point for both endpoints, same as every
 * other `isSuperAdmin` check trusted client-side in `~/utils/guards.ts`.
 */
export function useConfigurationScope(): ConfigurationScope {
  const user = useAuthStore((s) => s.user);
  const activeTenant = user?.accessibleTenants.find((t) => t.id === user.activeTenantId);

  if (user?.isSuperAdmin && activeTenant?.isSystem) {
    return { mode: 'global' };
  }

  return {
    mode: 'subsidiary',
    subsidiaryId: user?.activeTenantId ?? '',
    subsidiaryName: activeTenant?.name ?? '',
  };
}
